import type {
  DeletionPropagationState,
  DeletionTarget,
  PrivacyRequestDetailDto,
} from '@devmil/contracts';

import {formatDateTime, UNAVAILABLE} from '../../lib/format.js';

/**
 * As quatro coisas que a tela precisa manter separadas.
 *
 * Elas são parecidas de longe e diferentes onde importa:
 *
 * 1. **Thiago autenticado.** Prova que quem está no painel é o dono. Não prova
 *    NADA sobre o titular do dado.
 * 2. **Titular verificado.** Prova que a pessoa do outro lado é quem diz ser,
 *    pelo canal dela. É esta, e só esta, que libera executar um direito.
 * 3. **Entrega emitida.** O pacote existe e tem prazo.
 * 4. **Entrega consumida ou vencida.** O pacote já não pode ser aberto.
 *
 * Juntar a primeira com a segunda é o erro que entrega dado de terceiro a quem
 * pediu por engano, e é exatamente o erro que esta tela existe para evitar.
 */

const ALVO_LABEL: Record<DeletionTarget, string> = {
  postgres: 'Banco de dados',
  object_storage: 'Armazenamento de objetos',
  search_index: 'Índice de busca',
  application_logs: 'Logs da aplicação',
  n8n_execution_data: 'Execuções do n8n',
};

const PROPAGACAO_LABEL: Record<DeletionPropagationState, string> = {
  pending: 'PENDENTE',
  completed: 'CONCLUÍDA',
  // "Provado que não se aplica" é diferente de "não olhei". O segundo é
  // `pending`, e confundir os dois faz uma exclusão parecer completa.
  not_applicable_verified: 'NÃO SE APLICA, PROVADO',
  failed: 'FALHOU',
};

const PROPAGACAO_CLASSE: Record<DeletionPropagationState, string> = {
  pending: 'dm-ok is-degraded',
  completed: 'dm-ok',
  not_applicable_verified: 'dm-ok',
  failed: 'dm-ok is-failed',
};

type DeliveryState = NonNullable<PrivacyRequestDetailDto['delivery']>['state'];

const EMITIDA_LABEL: Record<DeliveryState, string> = {
  none: 'NÃO EMITIDA',
  ready: 'EMITIDA',
  consumed: 'EMITIDA',
  expired: 'EMITIDA',
};

const FIM_LABEL: Record<DeliveryState, string> = {
  none: 'NÃO SE APLICA',
  ready: 'AINDA VÁLIDA',
  consumed: 'CONSUMIDA',
  expired: 'VENCIDA',
};

const FIM_CLASSE: Record<DeliveryState, string> = {
  none: 'dm-ok is-degraded',
  ready: 'dm-ok',
  consumed: 'dm-ok is-degraded',
  expired: 'dm-ok is-failed',
};

export const PrivacyDeliveryStatus = ({
  request,
}: {
  request: PrivacyRequestDetailDto;
}) => {
  const {delivery, subjectVerifiedAt} = request;

  return (
    <section className="dm-panel" aria-label="Provas e entrega">
      <div className="dm-panel-head">
        <h2>Provas e entrega</h2>
        <span className="dm-illustrative">
          <code>{request.operationAuditRef.slice(0, 8)}</code>
        </span>
      </div>

      <ul className="dm-checklist">
        <li>
          <span>Sessão do painel (Thiago)</span>
          <strong className="dm-ok">AUTENTICADA</strong>
        </li>
        <li>
          <span>Prova do titular</span>
          <strong className={subjectVerifiedAt ? 'dm-ok' : 'dm-ok is-failed'}>
            {subjectVerifiedAt
              ? `VERIFICADO EM ${formatDateTime(subjectVerifiedAt)}`
              : 'NÃO VERIFICADO'}
          </strong>
        </li>
        <li>
          <span>Entrega emitida</span>
          <strong>
            {/* `delivery` nulo é ausência de informação, e ausência não vira
                "não emitida": zero é uma medição, não saber é outra coisa. */}
            {delivery ? EMITIDA_LABEL[delivery.state] : UNAVAILABLE}
          </strong>
        </li>
        <li>
          <span>Entrega consumida ou vencida</span>
          <strong className={delivery ? FIM_CLASSE[delivery.state] : undefined}>
            {delivery ? FIM_LABEL[delivery.state] : UNAVAILABLE}
          </strong>
        </li>
        <li>
          <span>Vencimento da entrega</span>
          <strong>
            {delivery?.expiresAt
              ? formatDateTime(delivery.expiresAt)
              : UNAVAILABLE}
          </strong>
        </li>
        <li>
          <span>Prazo legal do pedido</span>
          <strong>{formatDateTime(request.dueAt)}</strong>
        </li>
      </ul>

      {request.propagations.length > 0 ? (
        <>
          <h3 className="dm-kicker">Propagação da exclusão</h3>
          <ul className="dm-checklist">
            {request.propagations.map((propagation) => (
              <li key={propagation.target}>
                <span>{ALVO_LABEL[propagation.target]}</span>
                <strong className={PROPAGACAO_CLASSE[propagation.state]}>
                  {PROPAGACAO_LABEL[propagation.state]}
                </strong>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
};
